import { type Subtract } from "utility-types";

import { type Prettify } from "@north-beam/nb-common";

import { type AbstractUser } from "../../../user";
import { type LoggerEnv } from "../constants";

import { type LoggerLocation, type LoggerParsedLocation } from "./location";

export type LoggerContext<U extends AbstractUser> = Partial<{
  readonly previousLocation: LoggerLocation | LoggerParsedLocation;
  readonly location: LoggerLocation | LoggerParsedLocation;
  readonly user: U;
  [key: string]: unknown;
}>;

type LoggerUserContext = {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly timezone: string;
  readonly impersonatedUserId?: string;
  readonly isNorthbeamUser: boolean;
};

export type ParsedLoggerUserContext<
  U extends AbstractUser,
  C extends LoggerContext<U>,
> = C extends { user: U } ? LoggerUserContext : Partial<LoggerUserContext>;

type CommonContext<U extends AbstractUser, C extends LoggerContext<U>> = {
  readonly module: string;
  readonly environment: string;
  readonly nodeEnvironment: LoggerEnv;
  readonly user: ParsedLoggerUserContext<U, C>;
  readonly previousLocation?: LoggerParsedLocation;
};

type _Parsed<U extends AbstractUser, C extends LoggerContext<U>> = C extends {
  location: LoggerLocation;
  user: U;
}
  ? {
      readonly user: LoggerUserContext;
      readonly location: LoggerParsedLocation;
    }
  : C extends { location?: LoggerLocation; user: U }
  ? {
      readonly user: LoggerUserContext;
      readonly location?: LoggerParsedLocation;
    }
  : C extends { location: LoggerLocation; user?: U }
  ? {
      readonly user?: LoggerUserContext;
      readonly location: LoggerParsedLocation;
    }
  : {
      readonly user?: LoggerUserContext;
      readonly location?: LoggerParsedLocation;
    };

export type ParsedLoggerContext<
  U extends AbstractUser,
  C extends LoggerContext<U>,
> = Prettify<
  Subtract<C, LoggerContext<U>> & _Parsed<U, C> & CommonContext<U, C>
>;

export const TRANSFORMED_LOGGER_CONTEXT_KEYS = [
  "user",
  "module",
  "environment",
  "nodeEnvironment",
  "location",
  "previousLocation",
] as const;

export const prettifyContext = <T extends Record<string, unknown>>(obj: T): T =>
  Object.keys(obj).reduce(<K extends keyof T>(prev: T, curr: K) => {
    const v = obj[curr];
    if (typeof v === "object" && v !== null) {
      const reduced: (T[K] & Record<string, unknown>) | null = prettifyContext(
        v as T[K] & Record<string, unknown>,
      );
      if (Object.keys(reduced).length === 0) {
        return prev;
      }
      return { ...prev, [curr]: reduced };
    } else if (v !== undefined) {
      return {
        ...prev,
        [curr]: v,
      };
    }
    return prev;
  }, {} as T);
